import React, { useState, useEffect, useMemo } from 'react';
import axiosInstance from "../api/axiosInstance";
import { useAuth } from "../context/AuthContext";
import { Alert, Button, Form, Input, Modal, Select, Table } from 'antd';
import {
  CardTitle,
  DetailList,
  ListToolbar,
  PageHeader,
  RowActions,
  Section,
  SectionCard,
  StatusTag,
  TabBar,
  tablePagination,
  useFlatPage
} from '../components/ui';
import { IconInfo } from '../components/ui/icons';
import notify from "../utils/notify";

const { TextArea } = Input;
const { Option } = Select;

const RoomMaintenancePage = () => {
  useFlatPage();
  const { authData } = useAuth();
  const [tasks, setTasks] = useState([]);
  const [loading, setLoading] = useState(false);
  const [currentTask, setCurrentTask] = useState(null);
  const [assignModalOpen, setAssignModalOpen] = useState(false);
  const [staff, setStaff] = useState([]);
  const [assignForm] = Form.useForm();
  const [apiErrors, setApiErrors] = useState(null);
  const [filterStatus, setFilterStatus] = useState('All');
  const [showInfoModal, setShowInfoModal] = useState(false);
  const [searchText, setSearchText] = useState('');

  const statusOptions = [
    { value: 'Pending', label: 'Pending' },
    { value: 'InProgress', label: 'In progress' },
    { value: 'Completed', label: 'Completed' },
    { value: 'Cancelled', label: 'Cancelled' },
  ];

  const priorityOptions = [
    { value: 'Low', label: 'Low' },
    { value: 'Medium', label: 'Medium' },
    { value: 'High', label: 'High' },
    { value: 'Emergency', label: 'Emergency' },
  ];

  useEffect(() => {
    if (authData?.hotelId) {
      fetchTasks();
      fetchMaintenanceStaff();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [authData?.hotelId]);

  const fetchTasks = async () => {
    setLoading(true);
    try {
      const response = await axiosInstance.get(`/Maintenance/GetAllMaintenanceTasks?hotelId=${authData.hotelId}`);
      setTasks((response.data || []).map(task => ({
        ...task,
        key: task.maintenanceTaskId,
      })));
      setApiErrors(null);
    } catch (err) {
      console.error("Error fetching tasks:", err);
      setApiErrors(err.response?.data);
      notify.error({
        title: "Error",
        description: err.response?.data?.message || "Failed to load maintenance tasks"
      });
    } finally {
      setLoading(false);
    }
  };

  const fetchMaintenanceStaff = async () => {
    try {
      const response = await axiosInstance.get("/User/WithPermission", { 
        params: { 
          hotelId: authData.hotelId,
          permission: "MaintenanceAssignment" 
        } 
      });
      setStaff(response.data.users || []);
    } catch (err) {
      console.error("Error fetching staff:", err);
      notify.error({
        title: "Error",
        description: err.response?.data?.message || "Failed to load maintenance staff"
      });
    }
  };

  const handleAssignTask = async (values) => {
    try {
      const response = await axiosInstance.post('/Maintenance/Assign', {
        ...values,
        maintenanceTaskId: currentTask.maintenanceTaskId,
        hotelId: authData.hotelId
      });
      
      // Update the task in state with the assigned user
      const updatedTasks = tasks.map(task => 
        task.maintenanceTaskId === currentTask.maintenanceTaskId
          ? {
              ...task,
              assignedTo: staff.find(user => user.userId === values.userId),
              assignedToUserId: values.userId,
              assignmentNotes: values.assignmentNotes
            }
          : task
      );
      
      setTasks(updatedTasks);
      notify.success({
        title: 'Success',
        description: 'Task assigned successfully'
      });
      setAssignModalOpen(false);
    } catch (err) {
      console.error("Error assigning task:", err);
      setApiErrors(err.response?.data);
      notify.error({
        title: "Error",
        description: err.response?.data?.message || "Failed to assign task"
      });
    }
  };

 const handleCompleteTask = async (taskId) => {
  try {
    const taskToComplete = tasks.find(t => t.maintenanceTaskId === taskId);
    
    if (!taskToComplete) {
      notify.error({
        title: 'Error',
        description: 'Task not found in local state'
      });
      return;
    }

    // First complete the task
    const response = await axiosInstance.put(`/Maintenance/CompleteTask/${taskId}`, {
      hotelId: authData.hotelId  // Make sure this is included
    });

    if (response.status === 200) {
      notify.success({
        title: 'Success',
        description: 'Task marked as completed'
      });

      // Then update room status if applicable
      if (taskToComplete.roomId) {
        try {
          await axiosInstance.put(`/Rooms/UpdateStatus/${taskToComplete.roomId}`, {
            status: 'Available',
            hotelId: authData.hotelId
          });
        } catch (roomError) {
          console.error("Room status update failed:", roomError);
          notify.warning({
            title: 'Note',
            description: 'Task completed but room status could not be updated'
          });
        }
      }

      // Refresh the task list
      fetchTasks();
    }
  } catch (err) {
    console.error("Error completing task:", err);
    let errorMessage = "Failed to complete task";
    
    if (err.response) {
      if (err.response.status === 404) {
        errorMessage = "Task not found - it may have been deleted";
      } else if (err.response.data?.message) {
        errorMessage = err.response.data.message;
      }
    }

    notify.error({
      title: "Error",
      description: errorMessage
    });
  }
};
  const renderStatusTag = (status) => {
    const config = statusOptions.find(opt => opt.value === status);
    return <StatusTag status={status}>{config?.label || status}</StatusTag>;
  };

  const renderPriorityTag = (priority) => <StatusTag status={priority}>{priority || 'Unknown'}</StatusTag>;

  const counts = useMemo(() => {
    const c = {};
    tasks.forEach((x) => {
      c[x.status] = (c[x.status] || 0) + 1;
    });
    return c;
  }, [tasks]);

  const tabs = [
    { key: 'All', label: 'All', count: tasks.length },
    ...statusOptions.map((o) => ({ key: o.value, label: o.label, count: counts[o.value] || 0 }))
  ];

  const visibleTasks = useMemo(() => {
    const q = searchText.trim().toLowerCase();
    return tasks.filter((t) => {
      if (filterStatus !== 'All' && t.status !== filterStatus) return false;
      if (!q) return true;
      return `${t.title || ''} ${t.roomNumber || ''} ${t.location || ''}`.toLowerCase().includes(q);
    });
  }, [tasks, filterStatus, searchText]);

  const openAssign = (record) => {
    setCurrentTask(record);
    assignForm.setFieldsValue({ userId: record.assignedToUserId || null, assignmentNotes: record.assignmentNotes || '' });
    setAssignModalOpen(true);
  };

  const columns = [
    {
      title: 'Task',
      dataIndex: 'title',
      key: 'title',
      sorter: (a, b) => a.maintenanceTaskId - b.maintenanceTaskId,
      render: (text, r) => (
        <div className="hms-cell-stack">
          <span className="hms-cell-main">{text}</span>
          <span className="hms-cell-sub">#{r.maintenanceTaskId}</span>
        </div>
      )
    },
    {
      title: 'Room',
      dataIndex: 'roomNumber',
      key: 'roomNumber',
      render: (text, record) => text || (record.roomId ? `Room ID ${record.roomId}` : record.location || '—')
    },
    { title: 'Status', dataIndex: 'status', key: 'status', width: 140, render: renderStatusTag },
    { title: 'Priority', dataIndex: 'priority', key: 'priority', width: 130, render: renderPriorityTag },
    {
      title: 'Assigned to',
      key: 'assignedTo',
      render: (_, record) =>
        record.assignedTo ? (
          <div className="hms-cell-stack">
            <span className="hms-cell-main">{record.assignedTo.userName}</span>
            <span className="hms-cell-sub">{record.assignedTo.role}</span>
          </div>
        ) : (
          <span className="hms-muted">Not assigned</span>
        )
    },
    {
      title: '',
      key: 'action',
      fixed: 'right',
      align: 'right',
      width: 180,
      render: (_, record) => (
        <RowActions
          actions={[
            {
              key: 'complete',
              label: 'Complete',
              icon: 'accept',
              showLabel: true,
              tone: 'primary',
              hidden: record.status === 'Completed' || record.status === 'Cancelled',
              onClick: () => handleCompleteTask(record.maintenanceTaskId)
            },
            { key: 'assign', label: record.assignedTo ? 'Reassign task' : 'Assign task', icon: 'assign', onClick: () => openAssign(record) }
          ]}
        />
      )
    }
  ];

  return (
    <div className="hms-page rm-page">
      <PageHeader
        actions={
          <Button icon={<IconInfo />} onClick={() => setShowInfoModal(true)}>
            Guide
          </Button>
        }
      />

      {apiErrors && (
        <Alert
          title="Something went wrong"
          description={apiErrors.message || 'An error occurred'}
          type="error"
          showIcon
          closable
          style={{ marginBottom: 12 }}
          onClose={() => setApiErrors(null)}
        />
      )}

      <SectionCard
        flush
        title={<CardTitle count={tasks.length}>Room maintenance</CardTitle>}
        extra={
          <ListToolbar
            search={{ value: searchText, onChange: setSearchText, placeholder: 'Search tasks' }}
            onRefresh={fetchTasks}
            loading={loading}
          />
        }
      >
        <TabBar tabs={tabs} value={filterStatus} onChange={setFilterStatus} />
        <Table
          className="hms-table"
          columns={columns}
          dataSource={visibleTasks}
          loading={loading}
          rowKey="maintenanceTaskId"
          pagination={tablePagination('task')}
          scroll={{ x: 900 }}
          locale={{ emptyText: <div className="hms-empty">No maintenance tasks in this view</div> }}
        />
      </SectionCard>

      <Modal
        title={`Assign task #${currentTask?.maintenanceTaskId || ''}`}
        open={assignModalOpen}
        onCancel={() => setAssignModalOpen(false)}
        footer={null}
        width={480}
        forceRender
      >
        <Form form={assignForm} layout="vertical" onFinish={handleAssignTask}>
          <Form.Item label="Assign to" name="userId" rules={[{ required: true, message: 'Please select a staff member' }]}>
            <Select
              placeholder={staff.length === 0 ? 'No staff available' : 'Select maintenance staff'}
              disabled={staff.length === 0}
            >
              {staff.map((user) => (
                <Option key={user.userId} value={user.userId}>
                  {user.userName} ({user.role})
                </Option>
              ))}
            </Select>
          </Form.Item>
          <Form.Item label="Assignment notes" name="assignmentNotes">
            <TextArea rows={3} placeholder="Any special instructions" />
          </Form.Item>
          <div className="hms-form-actions">
            <Button onClick={() => setAssignModalOpen(false)}>Cancel</Button>
            <Button type="primary" htmlType="submit">
              Assign task
            </Button>
          </div>
        </Form>
      </Modal>

      <Modal
        title="Room maintenance guide"
        open={showInfoModal}
        onCancel={() => setShowInfoModal(false)}
        footer={[
          <Button key="close" type="primary" onClick={() => setShowInfoModal(false)}>
            Got it
          </Button>
        ]}
        width={560}
        destroyOnHidden
      >
        <p className="hms-modal-note">Track and manage maintenance work on rooms.</p>
        <Section title="How to use it">
          <ol style={{ paddingLeft: 20, margin: 0 }}>
            <li>Use the tabs to see tasks by status.</li>
            <li>Assign a staff member to a task.</li>
            <li>Click Complete when the work is finished; the room goes back to Available.</li>
          </ol>
        </Section>
        <Section title="Priority levels">
          <DetailList
            items={[
              { label: 'Low', value: "Minor issues that don't affect the room" },
              { label: 'Medium', value: 'Issues that affect the guest experience' },
              { label: 'High', value: 'Critical issues affecting room functionality' },
              { label: 'Emergency', value: 'Immediate attention required (safety issues)' }
            ]}
          />
        </Section>
      </Modal>
    </div>
  );
};

export default RoomMaintenancePage;
